import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, User, Building2, Banknote, Calendar, BookOpen, Loader2,
  LayoutDashboard, UserCircle, Users, MessageSquare, Bookmark, Settings,
  ArrowRight,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import api, { fileUrl } from "@/lib/api";
import { trackResult } from "@/lib/analytics";
import { useAnalyticsImpressions } from "@/hooks/useAnalyticsImpressions";

const QUICK_LINKS = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "My Profile", to: "/dashboard/profile", icon: UserCircle },
  { label: "Network", to: "/dashboard/network", icon: Users },
  { label: "Messages", to: "/dashboard/messages", icon: MessageSquare },
  { label: "Bookmarks", to: "/dashboard/bookmarks", icon: Bookmark },
  { label: "My Events", to: "/dashboard/events", icon: Calendar },
  { label: "My Resources", to: "/dashboard/resources", icon: BookOpen },
  { label: "Account Settings", to: "/dashboard/account", icon: Settings },
  { label: "Browse People", to: "/people", icon: User },
  { label: "Browse Businesses", to: "/businesses", icon: Building2 },
  { label: "Browse Capital", to: "/capital", icon: Banknote },
];

const EMPTY = { people: [], businesses: [], capital: [], events: [], resources: [], chatter: [] };

const PROFILE_PATH = { personal: "p", business: "b", capital: "capital" };

const Thumb = ({ src, round, icon: Icon }) => (
  <div
    className={`flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden bg-secondary text-muted-foreground ${round ? "rounded-full" : ""}`}
  >
    {src ? (
      <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" />
    ) : (
      <Icon className="h-4 w-4" />
    )}
  </div>
);

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  // Global Cmd/Ctrl+K toggle
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // Reset query whenever the dialog opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setResults(EMPTY);
    }
  }, [open]);

  // Debounced server search
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const { data } = await api.get("/search", {
          params: { q: term, surface: "command_palette" },
          signal: controller.signal,
        });
        setResults({ ...EMPTY, ...data });
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") setResults(EMPTY);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const go = useCallback(
    (to) => {
      onOpenChange(false);
      navigate(to);
    },
    [navigate, onOpenChange]
  );

  // Result selection — mouse OR keyboard both land here. Analytics is fire-and-forget:
  // navigation is never delayed and never awaits the beacon.
  const bindImpression = useAnalyticsImpressions("search_impression");
  const pick = useCallback(
    (analytics, to) => {
      trackResult("search_result_clicked", analytics);
      go(to);
    },
    [go]
  );

  const term = query.trim().toLowerCase();
  const quick = term
    ? QUICK_LINKS.filter((l) => l.label.toLowerCase().includes(term))
    : QUICK_LINKS;

  const totalResults =
    results.people.length + results.businesses.length + results.capital.length +
    results.events.length + results.resources.length + results.chatter.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl gap-0 overflow-hidden p-0"
        data-testid="command-palette"
      >
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <DialogDescription className="sr-only">Search people, businesses, capital, events and resources.</DialogDescription>
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search people, businesses, capital, events, resources…"
            data-testid="command-palette-input"
          />
          <CommandList className="max-h-[60vh]">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground" data-testid="command-palette-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
              </div>
            )}
            {!loading && term && totalResults === 0 && (
              <CommandEmpty data-testid="command-palette-empty">
                No matches for "{query}".
              </CommandEmpty>
            )}

            {quick.length > 0 && (
              <CommandGroup heading={term ? "Quick links" : "Go to"}>
                {quick.map((l) => (
                  <CommandItem
                    key={l.to}
                    value={`nav-${l.to}`}
                    onSelect={() => go(l.to)}
                    data-testid={`command-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                    className="cursor-pointer"
                  >
                    <l.icon className="h-4 w-4 text-muted-foreground" />
                    <span>{l.label}</span>
                    <ArrowRight className="ml-auto h-3.5 w-3.5 opacity-40" />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.people.length > 0 && (
              <CommandGroup heading="People">
                {results.people.map((p) => (
                  <CommandItem key={`p-${p.slug}`} value={`person-${p.slug}`} onSelect={() => pick(p.analytics, `/p/${p.slug}`)} className="cursor-pointer" data-testid={`command-person-${p.slug}`} ref={bindImpression(p.analytics)}>
                    <Thumb src={p.headshot_url} round icon={User} />
                    <div className="min-w-0">
                      <div className="truncate font-[560]">{p.display_name}</div>
                      {p.headline && <div className="truncate text-xs text-muted-foreground">{p.headline}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.businesses.length > 0 && (
              <CommandGroup heading="Businesses">
                {results.businesses.map((b) => (
                  <CommandItem key={`b-${b.slug}`} value={`business-${b.slug}`} onSelect={() => pick(b.analytics, `/b/${b.slug}`)} className="cursor-pointer" data-testid={`command-business-${b.slug}`} ref={bindImpression(b.analytics)}>
                    <Thumb src={b.profile_image_url} icon={Building2} />
                    <div className="min-w-0">
                      <div className="truncate font-[560]">{b.display_name}</div>
                      {b.headline && <div className="truncate text-xs text-muted-foreground">{b.headline}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.capital.length > 0 && (
              <CommandGroup heading="Capital">
                {results.capital.map((c) => (
                  <CommandItem key={`c-${c.slug}`} value={`capital-${c.slug}`} onSelect={() => pick(c.analytics, `/capital/${c.slug}`)} className="cursor-pointer" data-testid={`command-capital-${c.slug}`} ref={bindImpression(c.analytics)}>
                    <Thumb src={c.profile_image_url} icon={Banknote} />
                    <div className="min-w-0">
                      <div className="truncate font-[560]">{c.display_name}</div>
                      {c.headline && <div className="truncate text-xs text-muted-foreground">{c.headline}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.events.length > 0 && (
              <CommandGroup heading="Events">
                {results.events.map((ev) => (
                  <CommandItem key={`e-${ev.slug}`} value={`event-${ev.slug}`} onSelect={() => pick(ev.analytics, `/event/${ev.slug}`)} className="cursor-pointer" data-testid={`command-event-${ev.slug}`} ref={bindImpression(ev.analytics)}>
                    <Thumb src={ev.cover_url} icon={Calendar} />
                    <div className="min-w-0">
                      <div className="truncate font-[560]">{ev.title}</div>
                      {ev.summary && <div className="truncate text-xs text-muted-foreground">{ev.summary}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.resources.length > 0 && (
              <CommandGroup heading="Resources">
                {results.resources.map((r) => (
                  <CommandItem key={`r-${r.slug}`} value={`resource-${r.slug}`} onSelect={() => pick(r.analytics, `/resource/${r.slug}`)} className="cursor-pointer" data-testid={`command-resource-${r.slug}`} ref={bindImpression(r.analytics)}>
                    <Thumb src={r.cover_url} icon={BookOpen} />
                    <div className="min-w-0">
                      <div className="truncate font-[560]">{r.title}</div>
                      {r.summary && <div className="truncate text-xs text-muted-foreground">{r.summary}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.chatter.length > 0 && (
              <CommandGroup heading="Chatter">
                {results.chatter.map((c) => (
                  <CommandItem key={`ch-${c.post_id}`} value={`chatter-${c.post_id}`} onSelect={() => pick(c.analytics, `/${PROFILE_PATH[c.profile_type] || "p"}/${c.slug}`)} className="cursor-pointer" data-testid={`command-chatter-${c.post_id}`} ref={bindImpression(c.analytics)}>
                    <Thumb src={null} icon={MessageSquare} />
                    <div className="min-w-0">
                      <div className="truncate font-[560]">{c.snippet || "Update"}</div>
                      <div className="truncate text-xs text-muted-foreground">{[c.author_name, c.profile_name].filter(Boolean).join(" · ")}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
